import { _Label } from '@common-models/label';
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import { selectUserObject } from '@common-reducers/UserSelectors';
import { filterChatsByLabelIdThunk } from '@common-reducers/labels-reducer/labels-thunks';
import { labelBadge } from '@common-reducers/labels-reducer/label-view-stats';
import { getContactLabelsCount } from '@common-reducers/labels-reducer/selectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import * as LucideIcons from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import LabelMoreOption from '../poppers/label-options';

type Props = {
    label: _Label
    isActive: boolean
    inboxCount: number
    unreadCount: number
}

// Map label names to lucide-react icons or use emoji from label data
const getLabelIcon = (label: _Label, isActive: boolean) => {
    const iconClass = isActive ? 'text-teal-600 dark:text-[var(--color-accent)]' : 'text-gray-500 dark:text-[var(--color-text-muted)]';
    const iconSize = 18;

    // If label has an emoji icon or lucide icon name
    if (label.icon && label.icon.trim()) {
        // Check if it's a valid Lucide icon name
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const DynamicIcon = (LucideIcons as any)[label.icon];
        if (DynamicIcon) {
            return <DynamicIcon size={iconSize} className={iconClass} />;
        }
        // Fallback to emoji text
        return <span className="text-lg">{label.icon}</span>;
    }
    return null;

};

export default memo(function TopHeaderLabelItem({ label, isActive, inboxCount, unreadCount }: Props) {
    const { t } = useTranslation(namespace)
    const dispatch = useAppDispatch()
    const contactLabelsCount = useAppSelector(state => getContactLabelsCount({ state, labelId: label._id ?? '' }))
    const authUser = useAppSelector(selectUserObject);
    const labelViewStats = useAppSelector(state => state.LabelReducer.labelViewStats)

    // Known system label names that should be translated
    const systemLabelNames = ['all', 'inbox', 'inbox zero', 'handle later', 'needs attention', 'unread', 'starred', 'done', 'snoozed'];

    // Get translated name for system labels
    const getTranslatedLabelName = (labelName: string, labelType?: string) => {
        // Check if it's a default type OR a known system label name
        if (labelType === 'default' || systemLabelNames.includes(labelName.toLowerCase())) {
            const key = `labels.systemLabels.${labelName.toLowerCase()}`;
            const translated = t(key);
            // If translation key exists (not same as key), use it; otherwise use original name
            return translated !== key ? translated : labelName;
        }
        return labelName;
    };

    async function handleFilter() {
        if (authUser?._id && label._id) {
            await dispatch(filterChatsByLabelIdThunk(label._id))
        } else {
            dispatch(accessFormPopupActions.setStatus('Login'))
        }
    }

    let badgeCount = contactLabelsCount;
    if (label.name === 'all') {
        badgeCount = 0;
    } else if (label.name?.toLowerCase() === 'inbox zero') {
        badgeCount = inboxCount;
    } else if (label.name?.toLowerCase() === 'unread') {
        badgeCount = unreadCount;
    }

    const activeStats = labelViewStats && labelViewStats.labelId === label._id ? labelViewStats.stats : null
    const badge = labelBadge(badgeCount, activeStats)

    return (
        <div className="relative flex items-center group">
            <button
                onClick={handleFilter}
                className={`
                    flex items-center gap-2 px-4 py-3 border-b-2 transition-all
                    ${isActive
                        ? 'border-teal-600 dark:border-[var(--color-accent)] text-teal-700 dark:text-[var(--color-accent)]'
                        : 'border-transparent text-gray-600 dark:text-[var(--color-text-muted)] hover:bg-gray-50 dark:hover:bg-[var(--label-hover)]'
                    }
                `}
            >
                {getLabelIcon(label, isActive)}
                <span className={`text-sm font-medium capitalize whitespace-nowrap ${isActive ? 'text-teal-700 dark:text-[var(--color-accent)]' : 'text-gray-700 dark:text-[var(--color-text-muted)]'}`}>
                    {getTranslatedLabelName(label.name ?? '', label.type)}
                </span>
                {badgeCount > 0 && (
                    <span
                        role={badge.total === null ? undefined : 'img'}
                        aria-label={badge.total === null ? undefined : t('labels.emptyView.badgeTitle', { visible: badge.visible, total: badge.total })}
                        className="bg-gray-200 dark:bg-[var(--status-badge-bg)] text-gray-700 dark:text-[var(--status-badge-text)] text-xs px-1.5 py-0.5 rounded font-medium">
                        {badge.text}
                    </span>
                )}
            </button>
            <div className="flex items-center">
                <LabelMoreOption label={label} />
            </div>
        </div>
    )
})
